import { ChangeEvent, FC, Fragment, ReactElement, useCallback, useMemo } from 'react';
import { makeObservable, observable, runInAction } from 'mobx';
import { Observer, observer } from 'mobx-react';

import { AggregateResult, GroupResult } from '@progress/kendo-data-query';
import { GridCellProps } from '@progress/kendo-react-grid';

import {
    BodyText,
    Button,
    Checkbox,
    Form,
    Headline,
    InputOnChangeData,
    Stack,
    Table,
    TableColumn,
    TableNoRecords,
} from '@servicetitan/design-system';

import { ApiGroup, ApiScopeGroup, WebhookEvent } from '../../api/app.api';
import { createSearchRegExp, SearchTextHighlighter } from '../../common';
import { WebhookEventViewModel } from '../models/webhook-event-view-model';
import { getDisplayNameString } from '../utils';
import { SearchIcon } from './icons';

class WebhookEventGridItem {
    constructor(
        private readonly scopeGroup: ApiScopeGroup,
        private readonly webhookEvent: WebhookEvent,
        private readonly viewModels: Map<string, WebhookEventViewModel>,
    ) {}

    get scopeGroupDisplayName(): string {
        return this.scopeGroup.displayName;
    }

    get webhookEventDisplayName(): string {
        return getDisplayNameString(this.webhookEvent);
    }

    get isSelected(): boolean {
        const viewModel = this.viewModels.get(this.webhookEvent.name);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        return viewModel !== undefined && viewModel.isSelected;
    }

    set isSelected(value: boolean) {
        const viewModel = this.viewModels.get(this.webhookEvent.name);
        if (viewModel) {
            viewModel.isSelected = value;
        } else if (value) {
            this.viewModels.set(this.webhookEvent.name, new WebhookEventViewModel(value, ''));
        }
    }

    get url(): string {
        return this.viewModels.get(this.webhookEvent.name)!.url;
    }

    set url(value: string) {
        this.viewModels.get(this.webhookEvent.name)!.url = value;
    }

    get urlValid(): boolean {
        return this.viewModels.get(this.webhookEvent.name)!.urlValid;
    }
}

class ScopeGroupGridItem implements GroupResult {
    readonly items: WebhookEventGridItem[];
    @observable expanded = true;

    constructor(
        private readonly scopeGroup: ApiScopeGroup,
        webhookEvents: WebhookEvent[], // the subset of scopeGroup.webhookEvents to display
        viewModels: Map<string, WebhookEventViewModel>,
    ) {
        this.items = webhookEvents.map(
            webhookEvent => new WebhookEventGridItem(scopeGroup, webhookEvent, viewModels),
        );
        makeObservable(this);
    }

    get aggregates(): AggregateResult {
        return {};
    }

    get field(): string {
        return 'scopeGroupDisplayName'; // name of WebhookEventGridItem property
    }

    get value(): string {
        return this.scopeGroup.displayName;
    }

    selectAll(value: boolean) {
        this.items.forEach(item => {
            item.isSelected = value;
        });
    }
}

const Empty: FC = () => null;

interface ScopeGroupHeaderCellProps {
    columnsCount: number;
    dataItem: ScopeGroupGridItem;
    expanded: boolean;
    globalSearchRegExp: RegExp | null;
}

const ScopeGroupHeaderCell: FC<ScopeGroupHeaderCellProps> = ({
    columnsCount,
    dataItem,
    expanded,
    globalSearchRegExp,
}) => {
    return (
        <Fragment>
            <td className="p-x-0-i ta-center-i">
                <Button
                    xsmall
                    fill="subtle"
                    iconName={expanded ? 'chevron_right' : 'expand_more'}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                    onClick={() => {
                        runInAction(() => {
                            dataItem.expanded = !dataItem.expanded;
                        });
                    }}
                />
            </td>
            <td>
                <Checkbox
                    checked={dataItem.items.every(item => item.isSelected)}
                    indeterminate={
                        dataItem.items.some(item => item.isSelected) &&
                        dataItem.items.some(item => !item.isSelected)
                    }
                    onChange={(_value, checked: boolean) => {
                        runInAction(() => {
                            dataItem.selectAll(checked);
                        });
                    }}
                />
            </td>
            <td colSpan={columnsCount - 2}>
                <Headline size="small" className="m-b-half">
                    <SearchTextHighlighter
                        content={dataItem.value}
                        globalSearchRegExp={globalSearchRegExp}
                    />
                </Headline>
            </td>
        </Fragment>
    );
};
ScopeGroupHeaderCell.displayName = 'ScopeGroupHeaderCell';

const CheckboxDataCell: FC<GridCellProps> = ({ dataItem, rowType }) => {
    if (rowType === 'data') {
        const gridItem = dataItem as WebhookEventGridItem;
        return (
            <td>
                <Checkbox
                    checked={gridItem.isSelected}
                    onChange={(_value, checked) => {
                        runInAction(() => {
                            gridItem.isSelected = checked;
                        });
                    }}
                />
            </td>
        );
    }
    return null;
};
CheckboxDataCell.displayName = 'CheckboxDataCell';

const UrlInput: FC<{ gridItem: WebhookEventGridItem; errorsVisible: boolean }> = ({
    gridItem,
    errorsVisible,
}) => (
    <Observer>
        {() =>
            gridItem.isSelected ? (
                <Form.Input
                    controlClassName="fs-2-i"
                    placeholder="https://"
                    value={gridItem.url}
                    error={
                        errorsVisible &&
                        !gridItem.urlValid && (
                            <Fragment>
                                Enter a valid URL beginning with <code>https://</code>.
                            </Fragment>
                        )
                    }
                    onChange={(_event: ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
                        runInAction(() => {
                            gridItem.url = data.value;
                        });
                    }}
                />
            ) : null
        }
    </Observer>
);
UrlInput.displayName = 'UrlInput';

export interface WebhookEventTableProps {
    apiGroup: ApiGroup;
    viewModels: Map<string, WebhookEventViewModel>;
    searchText: string;
    errorsVisible: boolean;
}

export const WebhookEventTable: FC<WebhookEventTableProps> = observer(
    ({ apiGroup, viewModels, searchText, errorsVisible }) => {
        const globalSearchRegExp = useMemo(
            () => createSearchRegExp(searchText, 'gi'),
            [searchText],
        );

        const SearchTextHighlighterCell: FC<GridCellProps> = useCallback(
            ({ dataItem, field, rowType }) =>
                rowType === 'data' ? (
                    <td>
                        <SearchTextHighlighter
                            content={dataItem[field!]}
                            globalSearchRegExp={globalSearchRegExp}
                        />
                    </td>
                ) : null,
            [globalSearchRegExp],
        );

        const UrlDataCell: FC<GridCellProps> = useCallback(
            ({ dataItem, rowType }) =>
                rowType === 'data' ? (
                    <td className="p-y-half-i">
                        <UrlInput
                            gridItem={dataItem as WebhookEventGridItem}
                            errorsVisible={errorsVisible}
                        />
                    </td>
                ) : null,
            [errorsVisible],
        );

        const cellRender = useCallback(
            (
                tdElement: ReactElement<HTMLTableCellElement> | null,
                { columnIndex, columnsCount, dataItem, expanded, rowType }: GridCellProps,
            ) => {
                return rowType === 'groupHeader' && columnIndex === 0 ? (
                    <ScopeGroupHeaderCell
                        columnsCount={columnsCount!}
                        dataItem={dataItem}
                        expanded={expanded!}
                        globalSearchRegExp={globalSearchRegExp}
                    />
                ) : (
                    tdElement
                );
            },
            [globalSearchRegExp],
        );

        const scopeGroupGridItems = useMemo(() => {
            const searchRegExp = createSearchRegExp(searchText, 'i'); // must not have the 'g' flag
            return apiGroup.scopeGroups
                .filter(
                    scopeGroup =>
                        scopeGroup.webhookEvents.length > 0 &&
                        (!searchRegExp ||
                            searchRegExp.test(scopeGroup.displayName) ||
                            scopeGroup.webhookEvents.some(webhookEvent =>
                                searchRegExp.test(webhookEvent.displayName),
                            )),
                )
                .map(
                    scopeGroup =>
                        new ScopeGroupGridItem(
                            scopeGroup,
                            !searchRegExp || searchRegExp.test(scopeGroup.displayName)
                                ? scopeGroup.webhookEvents
                                : scopeGroup.webhookEvents.filter(webhookEvent =>
                                      searchRegExp.test(webhookEvent.displayName),
                                  ),
                            viewModels,
                        ),
                );
        }, [apiGroup, viewModels, searchText]);

        /*
         * Because <Table> doesn't support MobX, we must read the value of every ScopeGroupGridItem.expanded property
         * ourselves in order for the table to be re-rendered when a group is expanded or collapsed,
         * and the value of every WebhookEventGridItem.isSelected property in order for the row to be re-rendered when
         * an event is selected or unselected.
         */
        scopeGroupGridItems.map(
            scopeGroupGridItem =>
                scopeGroupGridItem.expanded &&
                scopeGroupGridItem.items.map(
                    webhookEventGridItem => webhookEventGridItem.isSelected,
                ),
        ); // Discard the result.

        return (
            <Table
                data={scopeGroupGridItems}
                group={[{ field: 'scopeGroupDisplayName' }]}
                cellRender={cellRender}
                striped={false}
                expandField="expanded"
                selectable={{ enabled: true, mode: 'multiple' }}
                selectedField="isSelected"
                style={{ height: '100%' }}
            >
                <TableNoRecords>
                    <Stack direction="column" alignItems="center" spacing={2} className="m-y-2">
                        {SearchIcon}
                        <Stack.Item className="ta-center">
                            <Headline size="small" className="m-b-1">
                                No matching events found
                            </Headline>
                            <BodyText size="small" subdued>
                                Please adjust your search and try again.
                            </BodyText>
                        </Stack.Item>
                    </Stack>
                </TableNoRecords>
                <TableColumn
                    field="isSelected"
                    width={40}
                    headerCell={Empty}
                    cell={CheckboxDataCell}
                />
                <TableColumn
                    field="webhookEventDisplayName"
                    title="Event"
                    width="200px"
                    cell={SearchTextHighlighterCell}
                />
                <TableColumn field="url" title="Callback URL" cell={UrlDataCell} />
            </Table>
        );
    },
);
WebhookEventTable.displayName = 'WebhookEventTable';
